<template>
  <StaffLayout section="Clients">
    <div class="mb-4">
      <BackButton to="../clients">Back to Clients List</BackButton>
    </div>
    <div v-if="isLoading"><Loading /></div>
    <div v-else>
      <h1 class="py-3 mb-4 border-b flex">
        <div class="text-xl flex-grow">
          <div class="font-bold">Edit Client</div>
          <div>
            {{ client?.name }}
          </div>
        </div>
        <EditButton @click="editClientClick">Edit</EditButton>
      </h1>
      <div class="lg:flex flex-wrap">
        <div class="my-4 pr-4 lg:w-1/3">
          <h2 class="font-bold">Expire after months:</h2>
          <p>{{ client.expireAfterMonths }}</p>
        </div>
        <div class="my-4 pr-4 lg:w-1/3">
          <h2 class="font-bold">Minimum Passing Test Score:</h2>
          <p>{{ client.minimumScorePasses }}%</p>
        </div>
        <div class="my-4 pr-4 lg:w-1/3">
          <h2 class="font-bold">Category Alias:</h2>
          <p>{{ client.categoryAlias }}</p>
        </div>
        <div class="my-4 pr-4 lg:w-1/3">
          <h2 class="font-bold">Category Values:</h2>
          <div v-if="client?.categoryValues?.length ?? 0 > 0">
            <span
              class="font-mono text-sm"
              v-for="(category, i) in client.categoryValues"
              :key="i"
            >
              {{ category }}<span v-if="i + 1 !== client.categoryValues.length">; </span>
            </span>
          </div>
          <div v-else>
            <span class="mr-2">No Floors Specified</span>
          </div>
        </div>
        <div class="my-4 pr-4 lg:w-1/3">
          <h2 class="font-bold">Building Picker Enabled for All Users:</h2>
          <p>{{ client.enableBuildingPickerForAllUsers }}</p>
        </div>
      </div>
      <div v-if="client.note.length > 0" class="mt-4">
        <h2 class="font-bold">Notes:</h2>
        <p>{{ client.note }}</p>
      </div>
      <h2 class="font-bold pt-8">Portals:</h2>
      <div v-if="portals && portals.length > 0">
        <AddButton class="mt-2" @click="addPortalClick">Add Portal</AddButton>
        <div
          v-for="portal in portals"
          class="p-4 my-2 cursor-pointer hover:bg-blue-200 border-t"
          :class="portal.isActive ? 'bg-gray-100' : 'bg-gray-300'"
          @click="editPortalClick(portal)"
        >
          {{ portal.name }}
          <span v-if="!portal.isActive" class="text-sm">(disabled)</span>
        </div>
      </div>
      <div v-else class="my-2">-- No Portals found for this Client --</div>
      <AddButton @click="addPortalClick">Add Portal</AddButton>
      <EditPopup
        entity-name="Client"
        :is-visible="isClientPopupVisible"
        :is-edit-mode="true"
        :save-error="saveError"
        :delete-enabled="false"
        @close="isClientPopupVisible = false"
        @save="saveClientClick"
      >
        <BaseInput
          label="Client Name"
          v-model="clientName"
          type="text"
          :error="errorsClient.clientName"
        />
        <BaseInput
          label="System Name"
          v-model="systemName"
          type="text"
          :error="errorsClient.systemName"
        />
        <BaseInput
          label="Expire After Months"
          v-model="clientExpireAfterMonths"
          type="number"
          :error="errorsClient.clientExpireAfterMonths"
        />
        <BaseInput
          label="Minimum Passing Test Score (%)"
          v-model="clientMinimumScorePasses"
          type="number"
          :error="errorsClient.clientMinimumScorePasses"
        />
        <BaseInput
          label="Category Alias"
          v-model="clientCategoryAlias"
          type="text"
          :error="errorsClient.clientCategoryAlias"
        />
        <BaseInput
          label="Category Values (use a semicolon ';' between categories)"
          v-model="clientCategoryValues"
          type="text"
          :error="errorsClient.clientCategoryValues"
        />
        <BaseInput class="mb-4" label="Notes" v-model="clientNote" type="text" />
        <BaseCheckbox
          label="Enable Building Picker for All Users"
          v-model="clientEnableBuildingPickerForAllUsers"
          :error="errorsClient.clientEnableBuildingPickerForAllUsers"
        />
      </EditPopup>
      <EditPopup
        entity-name="Portal"
        :is-visible="isPortalPopupVisible"
        :is-edit-mode="isPortalEditing"
        :save-error="saveError"
        :delete-enabled="true"
        @close="isPortalPopupVisible = false"
        @save="savePortalClick"
        @delete="deletePortalClick"
      >
        <BaseInput
          v-model="propertyName"
          label="Portal Name"
          type="text"
          :error="errorsPortal.propertyName"
        />
        <BaseSelect
          v-model="propertyPropertyType"
          label="Property Type"
          :options="propertyTypes"
          :error="errorsPortal.propertyPropertyType"
        />
        <BaseInput
          v-model="portalSiteCode"
          label="Site Code"
          type="text"
          :error="errorsPortal.portalSiteCode"
        />
        <BaseSelect
          :options="client.categoryValues"
          v-model="propertyCategory"
          :label="`Client ${client.categoryAlias ?? 'Category'}`"
          :error="errorsPortal.propertyCategory"
        />
        <BaseInput v-model="propertyFloorList" label="Floor List" type="text" />
        <BaseInput
          v-model="propertyTenantAlias"
          label="Tenant Alias"
          type="text"
          :error="errorsPortal.propertyTenantAlias"
        />
        <BaseSelect
          v-model="portalResponseLibId"
          label="Response Library"
          :options="responseLibOptions"
          :error="errorsPortal.portalResponseLibId"
          :default-option="({ name: '--None--', value: -1 } as NameValue)"
        />
        <h2 class="pt-2 pb-1">Optional Features</h2>
        <BaseCheckbox
          v-model="portalIsTrainingEnabled"
          label="Enable Training"
          type="text"
        />
        <BaseCheckbox
          class="ml-1"
          v-model="portalShowExitButtonAtEndOfModule"
          label="Show Exit Button at End of Module"
          type="text"
          :disabled="!portalIsTrainingEnabled"
        />
        <BaseCheckbox
          class="ml-1"
          v-model="portalShowFinishButtonAtEndOfQuiz"
          label="Show Finish Button at End of Quiz"
          type="text"
          :disabled="!portalIsTrainingEnabled"
        />
        <BaseCheckbox
          v-model="portalIsRespondEnabled"
          label="Enable Respond"
          type="text"
        />
        <BaseCheckbox
          class="ml-1"
          v-model="portalRespondAnonView"
          label="Respond Anonymous Viewing"
          type="text"
          :disabled="!portalIsRespondEnabled"
        />
        <BaseCheckbox
          v-model="portalIsStaffDirEnabled"
          label="Enable Staff Directory"
          type="text"
        />
        <BaseCheckbox v-model="portalIsAlertEnabled" label="Enable Alerts" type="text" />
        <BaseCheckbox
          v-model="portalIsMobilityCheckEnabled"
          label="Enable Mobility Check"
          type="text"
        />
        <BaseCheckbox
          v-model="portalIsVrtourEnabled"
          label="Enable VR Tour"
          type="text"
        />
        <BaseCheckbox
          v-model="portalIsSharedPasswordMode"
          label="Enable Shared Passwords"
          type="text"
        />
        <div class="border-t border-gray-300 m-2 w-1/2" />
        <BaseCheckbox v-model="portalIsActive" label="Portal Is Active" type="text" />
      </EditPopup>
    </div>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useAdminStore } from "../../store/adminStore";
import { useBldgStore } from "../../store/bldgStore";
import { useRespondStore } from "../../store/respondStore";
import {
  PortalDTO,
  PropertyType,
  SaveClientCommand,
} from "../../store/generatedFetchClient";
//import { NameValue } from "../../types";
//😊

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { NameValue } from "../../sharedTypes";

const route = useRoute();
const adminStore = useAdminStore();
const bldgStore = useBldgStore();
const respondStore = useRespondStore();
let isLoading = ref(true);
let client = computed(() => adminStore.selectedClient);
let portals = computed(() => adminStore.selectedClientPortalList);

onMounted(async () => {
  console.log("Clients Admin Page mounted");
  isLoading.value = true;
  if (route.params.clientId && typeof route.params.clientId === "string") {
    let clientId = Number.parseInt(route.params.clientId);
    await adminStore.getClient(clientId);
    await adminStore.getPortalsByClient(clientId);
    await respondStore.getLibraryList();
  }
  isLoading.value = false;
});

// Popup stuff
const isClientPopupVisible = ref(false);
let saveError = ref("");

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

// Client Edit
const { handleSubmit: handleSubmitClient, errors: errorsClient } = useForm({
  validationSchema: yup.object({
    clientName: yup.string().required().max(100).label("Client Name"),
    systemName: yup
      .string()
      .required()
      .min(3)
      .max(100)
      .matches(/^[^\ \'\.\,\&\n\"]+$/, {
        message:
          "System Name cannot contain special characters such as spaces and quotes",
      })
      .label("System Name"),
    clientExpireAfterMonths: yup
      .number()
      .typeError("Expire After Months is a required field")
      .required()
      .label("Expire After Months"),
    clientMinimumScorePasses: yup
      .number()
      .typeError("Minimum Score Passes is a required field")
      .required()
      .min(0)
      .max(100)
      .label("Minimum Score Passes"),
    clientCategoryAlias: yup.string().required().max(50).label("Category Alias"),
    clientCategoryValues: yup.string().notRequired().label("Category Values"),
  }),
  initialValues: {
    clientName: "",
    systemName: "",
    clientExpireAfterMonths: 12,
    clientMinimumScorePasses: 80,
    clientCategoryAlias: "Region",
    clientCategoryValues: "",
    clientEnableBuildingPickerForAllUsers: false,
  },
});

const { value: clientName } = useField<string>("clientName");
const { value: systemName } = useField<string>("systemName");
const { value: clientExpireAfterMonths } = useField<number>("clientExpireAfterMonths");
const { value: clientMinimumScorePasses } = useField<number>("clientMinimumScorePasses");
const { value: clientCategoryAlias } = useField<string>("clientCategoryAlias");
const { value: clientCategoryValues } = useField<string>("clientCategoryValues");
let clientNote = ref("");
const { value: clientEnableBuildingPickerForAllUsers } = useField<boolean>(
  "clientEnableBuildingPickerForAllUsers"
);

let saveClientClick = handleSubmitClient(onValidSubmitClient, onInvalidSubmit);

async function onValidSubmitClient() {
  let saveClient = {
    clientId: client.value.clientId,
    name: clientName.value,
    expireAfterMonths: clientExpireAfterMonths.value,
    minimumScorePasses: clientMinimumScorePasses.value,
    categoryAlias: clientCategoryAlias.value,
    categoryValues: clientCategoryValues.value,
    note: clientNote.value,
    enableBuildingPickerForAllUsers: clientEnableBuildingPickerForAllUsers.value,
    systemName: systemName.value,
  } as SaveClientCommand;

  console.log("Saving client. ", saveClient);

  saveError.value = "";
  saveError.value = await adminStore.saveClient(saveClient);
  if (!saveError.value) {
    isClientPopupVisible.value = false;
    await adminStore.getClient(client.value.clientId);
  }
}

function editClientClick() {
  saveError.value = "";

  clientName.value = client.value.name ?? "";
  clientExpireAfterMonths.value = client.value.expireAfterMonths ?? 0;
  clientMinimumScorePasses.value = client.value.minimumScorePasses ?? 80;
  clientCategoryAlias.value = client.value.categoryAlias ?? "Region";
  clientCategoryValues.value = client.value.categoryValues.join(";");
  clientNote.value = client.value.note ?? "";
  clientEnableBuildingPickerForAllUsers.value =
    client.value.enableBuildingPickerForAllUsers;
  systemName.value = client.value.systemName ?? "";

  isClientPopupVisible.value = true;
}

// Portal Edit
let isPortalPopupVisible = ref(false);
let isPortalEditing = ref(false);

let propertyTypes: NameValue[] = [
  { name: "Mixed Use", value: 1 },
  { name: "Single Tenant", value: 2 },
  { name: "Residential", value: 3 },
  { name: "Multi-Building", value: 4 },
];

const { handleSubmit: handleSubmitPortal, errors: errorsPortal } = useForm({
  validationSchema: yup.object({
    portalSiteCode: yup.string().required().min(2).max(50).label("Site Code"),
    portalResponseLibId: yup.number().notRequired().label("Response Library"),
    propertyName: yup.string().required().max(100).label("Portal Name"),
    propertyCategory: yup
      .string()
      .notRequired()
      .max(50)
      .label("Client Region (Category)"),
    propertyPropertyType: yup
      .mixed<PropertyType>()
      .test("not-none", "Property Type is a required field", (value) => value > 0)
      .required()
      .label("Property Type"),
    propertyTenantAlias: yup.string().notRequired().max(50).label("Tenant Alias"),
  }),
  initialValues: {
    portalSiteCode: "",
    portalResponseLibId: -1,
    propertyName: "",
    propertyCategory: "",
    propertyPropertyType: PropertyType.None,
    propertyTenantAlias: "",
  },
});

let portalId = ref(-1);
const { value: portalSiteCode } = useField<string>("portalSiteCode");
const { value: portalResponseLibId } = useField<number | null>("portalResponseLibId");
let portalRespondAnonView = ref(false);
let portalIsTrainingEnabled = ref(false);
let portalShowExitButtonAtEndOfModule = ref(true);
let portalShowFinishButtonAtEndOfQuiz = ref(true);
let portalIsRespondEnabled = ref(false);
let portalIsStaffDirEnabled = ref(false);
let portalIsAlertEnabled = ref(false);
let portalIsMobilityCheckEnabled = ref(false);
let portalIsVrtourEnabled = ref(false);
let portalIsSharedPasswordMode = ref(false);
let portalIsActive = ref(false);

let propertyId = ref(-1);
const { value: propertyName } = useField<string>("propertyName");
const { value: propertyCategory } = useField<string>("propertyCategory");
const { value: propertyPropertyType } = useField<PropertyType>("propertyPropertyType");
let propertyFloorList = ref("");
const { value: propertyTenantAlias } = useField<string>("propertyTenantAlias");

watch(portalIsRespondEnabled, () => {
  if (!portalIsRespondEnabled.value) portalRespondAnonView.value = false;
});

const responseLibOptions = computed(
  () =>
    respondStore.libraryList.map((l) => ({
      name: l.name,
      value: l.responseLibId,
    })) as NameValue[]
);

let savePortalClick = handleSubmitPortal(onValidSubmitPortal, onInvalidSubmit);

async function onValidSubmitPortal() {
  let newPortal = {
    portalId: portalId.value,
    propertyId: propertyId.value,
    clientId: client.value.clientId,
    portalResponseLibId: portalResponseLibId.value > 0 ? portalResponseLibId.value : null,

    portalSiteCode: portalSiteCode.value,
    propertyName: propertyName.value,
    propertyCategory: propertyCategory.value,
    propertyPropertyType: propertyPropertyType.value.valueOf(),
    propertyFloorList: propertyFloorList.value,
    propertyTenantAlias: propertyTenantAlias.value,

    portalRespondAnonView: portalRespondAnonView.value,
    portalIsTrainingEnabled: portalIsTrainingEnabled.value,
    portalShowExitButtonAtEndOfModule: portalShowExitButtonAtEndOfModule.value,
    portalShowFinishButtonAtEndOfQuiz: portalShowFinishButtonAtEndOfQuiz.value,
    portalIsRespondEnabled: portalIsRespondEnabled.value,
    portalIsStaffDirEnabled: portalIsStaffDirEnabled.value,
    portalIsAlertEnabled: portalIsAlertEnabled.value,
    portalIsMobilityCheckEnabled: portalIsMobilityCheckEnabled.value,
    portalIsVrtourEnabled: portalIsVrtourEnabled.value,
    portalIsSharedPasswordMode: portalIsSharedPasswordMode.value,
    isActive: portalIsActive.value,
  };

  saveError.value = "";

  if (client.value) {
    saveError.value = await adminStore.savePortal(newPortal);
    await adminStore.getPortalsByClient(client.value.clientId);
    bldgStore.tryGetBuildingData(portalSiteCode.value, true);
    bldgStore.getPropertyList(true); // Refresh list for portal picker
    if (!saveError.value) {
      isPortalPopupVisible.value = false;
    }
  }
}

function addPortalClick() {
  portalId.value = -1;
  propertyId.value = -1;
  portalResponseLibId.value = -1;

  portalSiteCode.value = "";
  propertyName.value = "";
  propertyCategory.value = "";
  propertyPropertyType.value = PropertyType.None;
  propertyFloorList.value = "";
  propertyTenantAlias.value = "";

  portalRespondAnonView.value = false;
  portalIsTrainingEnabled.value = false;
  portalShowExitButtonAtEndOfModule.value = true;
  portalShowFinishButtonAtEndOfQuiz.value = true;
  portalIsRespondEnabled.value = false;
  portalIsStaffDirEnabled.value = false;
  portalIsAlertEnabled.value = false;
  portalIsMobilityCheckEnabled.value = false;
  portalIsVrtourEnabled.value = false;
  portalIsSharedPasswordMode.value = false;

  portalIsActive.value = true;

  saveError.value = "";
  isPortalEditing.value = false;
  isPortalPopupVisible.value = true;
}

function editPortalClick(portal: PortalDTO) {
  portalId.value = portal.portalId;
  propertyId.value = portal.propertyId;
  portalResponseLibId.value = portal.responseLibId ?? -1;

  portalSiteCode.value = portal.siteCode;
  propertyName.value = portal.name;
  propertyCategory.value = portal.category;
  propertyPropertyType.value = portal.propertyType;
  propertyFloorList.value = portal.floorList?.join(",") ?? "";
  propertyTenantAlias.value = portal.tenantAlias;

  portalRespondAnonView.value = portal.respondAnonView;
  portalIsTrainingEnabled.value = portal.isTrainingEnabled;
  portalShowExitButtonAtEndOfModule.value = portal.showExitButtonAtEndOfModule;
  portalShowFinishButtonAtEndOfQuiz.value = portal.showFinishButtonAtEndOfQuiz;
  portalIsRespondEnabled.value = portal.isRespondEnabled;
  portalIsStaffDirEnabled.value = portal.isStaffDirEnabled;
  portalIsAlertEnabled.value = portal.isAlertEnabled;
  portalIsMobilityCheckEnabled.value = portal.isMobilityCheckEnabled;
  portalIsVrtourEnabled.value = portal.isVrtourEnabled;
  portalIsSharedPasswordMode.value = portal.usesSharedPasswordMode;

  portalIsActive.value = portal.isActive ?? false;

  saveError.value = "";
  isPortalEditing.value = true;
  isPortalPopupVisible.value = true;
}

async function deletePortalClick() {
  if (portalId.value) {
    console.log(`Deleting Portal ${portalId.value}`);
    saveError.value = await adminStore.deletePortal(portalId.value);
    if (saveError.value) return;

    await adminStore.getPortalsByClient(client.value.clientId);
    bldgStore.getPropertyList(true); // Refresh list for portal picker
    bldgStore.building = null;
  }
  isPortalPopupVisible.value = false;
}
</script>
